import React, { useState } from "react";
import axios from "axios";
import { message } from "antd";
import Layout from "../components/Layout/Layout";
import "./NameCheck.css";

const REGION_MAP = {
  IN: "India",
  ID: "Indonesia",
  PH: "Philippines",
  SG: "Singapore",
  MY: "Malaysia",
  TH: "Thailand",
  VN: "Vietnam",
  KH: "Cambodia",
  MM: "Myanmar",
  LA: "Laos",
  BD: "Bangladesh",
  NP: "Nepal",
  PK: "Pakistan",
  LK: "Sri Lanka",
  BR: "Brazil",
  TR: "Turkey",
  SA: "Saudi Arabia",
  AE: "UAE",
  US: "United States",
  GB: "United Kingdom",
  JP: "Japan",
  KR: "South Korea",
};

function displayRegion(regionRaw) {
  const r = String(regionRaw || "").trim();
  if (!r) return "";
  const key = r.toUpperCase();
  if (key.length > 3) return r;
  return REGION_MAP[key] || r;
}

function normalizeGameData(raw) {
  const root = raw?.data || raw?.result || raw || {};

  const username = String(
    root?.username ||
      root?.nickname ||
      root?.name ||
      root?.player_name ||
      root?.ign ||
      ""
  ).trim();

  const regionRaw = String(
    root?.region ||
      root?.country ||
      root?.country_code ||
      root?.server_region ||
      ""
  ).trim();

  return {
    username,
    region: displayRegion(regionRaw),
  };
}

function normalizeBonusData(raw) {
  const username = String(raw?.username || "").trim();
  const userId = String(raw?.user_id || "").trim();

  const list = Array.isArray(raw?.bonus_limit) ? raw.bonus_limit : [];

  const products = list.map((item, idx) => {
    const reachedLimit = !!item?.reached_limit;

    return {
      id: `${item?.title || "bonus"}-${idx}`,
      title: item?.title || `Bonus ${idx + 1}`,
      available: !reachedLimit,
      reachedLimit,
    };
  });

  return {
    username,
    userId,
    products,
  };
}

export default function NameCheck() {
  const [id, setId] = useState("");
  const [zone, setZone] = useState("");
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();

    const cleanId = String(id || "").trim();
    const cleanZone = String(zone || "").trim();

    if (!cleanId) {
      message.error("Enter Player ID");
      return;
    }

    if (!cleanZone) {
      message.error("Enter Zone ID");
      return;
    }

    try {
      setLoading(true);
      setErrMsg("");
      setData(null);

      // 1) validate player
      const gameRes = await axios.get("/api/check/game", {
        params: {
          code: "mlbb",
          characterId: cleanId,
          server_code: cleanZone,
        },
      });

      if (!gameRes?.data?.success) {
        const msg = gameRes?.data?.message || "Failed to validate account";
        setErrMsg(msg);
        message.error(msg);
        return;
      }

      const gameInfo = normalizeGameData(gameRes.data);

      // 2) fetch MLBB bonus
      const bonusRes = await axios.get("/api/check/mlbb-bonus", {
        params: {
          user_id: cleanId,
          server_id: cleanZone,
        },
      });

      if (!bonusRes?.data?.success) {
        const msg = bonusRes?.data?.message || "Failed to load MLBB bonus";
        setErrMsg(msg);
        message.error(msg);
        return;
      }

      const bonusInfo = normalizeBonusData(bonusRes.data);

      const finalUsername = bonusInfo.username || gameInfo.username || "";
      const finalRegion = gameInfo.region || "";

      if (!finalUsername && !finalRegion && !bonusInfo.products.length) {
        setErrMsg("No data returned");
        message.error("No data returned");
        return;
      }

      setData({
        username: finalUsername,
        region: finalRegion,
        userId: bonusInfo.userId || cleanId,
        products: bonusInfo.products,
      });

      message.success("Check completed");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Request failed";

      setErrMsg(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setId("");
    setZone("");
    setData(null);
    setErrMsg("");
  };

  return (
    <Layout>
      <div className="ncPage">
        <div className="ncWrap">
          <div className="ncCard">
            <div className="ncHero ncHeroInside">
              <div className="ncHeroBadge">MLBB Tools</div>
              <h1 className="ncTitle">MLBB Name & Bonus Check</h1>
              <p className="ncSub">
                Check player information and see which MLBB double bonus packs
                are still available for this account.
              </p>
            </div>

            <div className="ncHeroDivider" />

            <form className="ncForm" onSubmit={submit}>
              <div className="ncGrid">
                <div className="ncRow">
                  <label className="ncLabel">Player ID</label>
                  <input
                    className="ncInput"
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                    placeholder="Enter Player ID"
                    inputMode="numeric"
                  />
                </div>

                <div className="ncRow">
                  <label className="ncLabel">Zone ID</label>
                  <input
                    className="ncInput"
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                    placeholder="Enter Zone ID"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="ncActions">
                <button
                  className="ncBtn ncBtnPrimary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Checking..." : "Check Now"}
                </button>

                <button
                  className="ncBtn ncBtnGhost"
                  type="button"
                  onClick={clear}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          {errMsg ? (
            <div className="ncResult ncResultBad">
              <div className="ncResultHead">
                <div className="ncBadge ncBadgeBad">FAILED</div>
                <div className="ncResultMsg">{errMsg}</div>
              </div>
            </div>
          ) : null}

          {data ? (
            <div className="ncResult ncResultOk">
              <div className="ncResultTop">
                <div>
                  <div className="ncSectionEyebrow">Account Details</div>
                  <h2 className="ncSectionTitle">Player Info</h2>
                </div>
                <div className="ncBadge ncBadgeOk">VALID</div>
              </div>

              <div className="ncInfoGrid">
                <div className="ncInfoCard">
                  <div className="ncInfoLabel">Player Name</div>
                  <div
                    className="ncInfoValue ncValueBlue"
                    title={data.username || "-"}
                  >
                    {data.username || "-"}
                  </div>
                </div>

                <div className="ncInfoCard">
                  <div className="ncInfoLabel">Region</div>
                  <div
                    className="ncInfoValue ncValueGreen"
                    title={data.region || "-"}
                  >
                    {data.region || "-"}
                  </div>
                </div>
              </div>

              <div className="ncDivider" />

              <div className="ncResultTop ncResultTopBonus">
                <div>
                  <div className="ncSectionEyebrow">Bonus Status</div>
                  <h2 className="ncSectionTitle">MLBB Double Bonus</h2>
                </div>
              </div>

              <div className="ncBonusList">
                {data.products?.length ? (
                  data.products.map((p) => {
                    const ok = !!p.available;

                    return (
                      <div className="ncBonusRow" key={p.id}>
                        <div className="ncBonusLeft">
                          <div className="ncBonusName">{p.title}</div>
                        </div>

                        <div className={`ncBonusRight ${ok ? "ok" : "bad"}`}>
                          <span className="ncBonusDot" />
                          <span>{ok ? "Available" : "Used / Limit Reached"}</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="ncEmpty">No bonus products returned.</div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}